import * as L from 'leaflet';
import type { IStopArea } from '../../../../models/stop-area.model';
import { TranslationService } from '../../../../services/translation.service';
import { ButtonDivIcon } from '../../../Shared/Map/markers/button-div-icon';
import { SanitizerService } from '../../../../services/sanitizer.service';

export default class StopAreaClusterMarker extends L.Marker {
  public id: string;
  private supportedTariffZoneNumbers = [3000, 3001, 3002];

  private translationStopArea: string;
  private translationTooltipMoreInfoLinkText: string;
  private translationTariffZone: string;
  private translationClosePopup: string;

  constructor(stopArea: IStopArea, onClick?: (clickedStopArea: IStopArea) => void | undefined, openPopup: boolean = false) {
    super([stopArea.latitude ?? 0, stopArea.longitude ?? 0], { zIndexOffset: 1000 });
    this.id = stopArea.gid;
    this.translationStopArea = TranslationService.get('common.stop');
    this.translationTooltipMoreInfoLinkText = TranslationService.get('stopareasearchpage.maptooltipmoreinfolinktext');
    this.translationTariffZone = TranslationService.get('common.tariffzone');
    this.translationClosePopup = TranslationService.get('common.map.closepopup');

    this.setIcon(
      new ButtonDivIcon({
        className: 'button-div-icon stop-area-marker',
        iconSize: [34, 55],
        iconAnchor: [17, 55],
        popupAnchor: [0, -50],
        html: `<img src="/icons/leaflet/map-marker-vasttrafik-blue-1000.svg" alt="${this.translationStopArea} ${SanitizerService.sanitizeHtml(stopArea.name)}" class="w-100 h-100" />`,
      })
    );

    this.bindPopup(this.createPopupHtml(stopArea), {
      autoPan: true,
      offset: [0, 0],
    });

    if (openPopup) {
      setTimeout(() => {
        this.openPopup();
      }, 200);
    }

    this.on('click', () => {
      if (onClick) onClick(stopArea);
    });

    this.on('popupopen', (popupEvent: L.PopupEvent) => {
      // Inspired by http://melmo.github.io/accessibility/code/dist/leaflet.html
      // Focus on the opened popup
      const stopAreaPopups = popupEvent.popup.getElement()?.getElementsByClassName('stop-area-popup') ?? [];
      if (stopAreaPopups.length > 0) {
        const stopAreaPopup = stopAreaPopups[0] as HTMLElement;
        stopAreaPopup.setAttribute('tabindex', '-1');
        stopAreaPopup.style.outline = 'none'; // Workaround for hiding built-in focus marker, see NV-4097
        stopAreaPopup.focus();
      }

      // Fix the a11y attributes on the close button
      const closeButtons = popupEvent.popup.getElement()?.getElementsByClassName('leaflet-popup-close-button') ?? [];
      if (closeButtons.length > 0) {
        const closeButton = closeButtons[0] as HTMLLinkElement;
        closeButton.setAttribute('role', 'button');
        closeButton.setAttribute('aria-label', this.translationClosePopup);
        closeButton.href = '#';
      }
    });

    this.on('popupclose', () => {
      this.removeQueryParam('hp');
    });
  }

  private createPopupHtml(stopArea: IStopArea): string {
    return `<div class="stop-area-popup">
      <h3 class="stop-area-popup__name">${stopArea.name}</h3>
      ${this.createTariffZoneHtmlElement(stopArea)}
      ${
        this.translationTooltipMoreInfoLinkText
          ? `<div class="text-end mt-2"><a href="${encodeURIComponent(stopArea.gid)}/" class="tooltip-more-link">${this.translationTooltipMoreInfoLinkText}</a></div>`
          : ''
      }
    </div>`;
  }

  private createTariffZoneHtmlElement(stopArea: IStopArea): string {
    let zoneSpanElements = '';

    [stopArea.tariffZone1, stopArea.tariffZone2]
      .filter((val) => val !== null && val !== undefined)
      .filter((val) => this.supportedTariffZoneNumbers.some((z) => z === val.number))
      .forEach((tariffZone) => {
        zoneSpanElements += `<small class="ms-1 tariff-zone-icon">${tariffZone.shortName}</small>`;
      });

    if (zoneSpanElements === '') return zoneSpanElements;

    return `<span class="d-flex">${this.translationTariffZone}${zoneSpanElements}</span>`;
  }

  private removeQueryParam(param: string) {
    const url = new URL(window.location.href);
    url.searchParams.delete(param);
    window.history.replaceState({}, '', url.toString());
  }
}
